import React, { useState, useEffect } from 'react';
import { Claim, RelatedClaim, SplitClaimsPayload, getRelatedClaims, splitClaims } from '../api/claims';
import './SplitClaimsModal.css';

interface SplitClaimsModalProps {
  isOpen: boolean;
  onClose: () => void;
  claim: Claim;
  onSuccess: (newEmail: string) => void;
}

interface NewClientData {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
}

type Step = 1 | 2 | 3;

const SplitClaimsModal: React.FC<SplitClaimsModalProps> = ({
  isOpen,
  onClose,
  claim,
  onSuccess
}) => {
  const [step, setStep] = useState<Step>(1);
  const [relatedClaims, setRelatedClaims] = useState<RelatedClaim[]>([]);
  const [selectedClaimIds, setSelectedClaimIds] = useState<Set<string>>(new Set());
  const [clientName, setClientName] = useState<string>('');
  const [clientEmail, setClientEmail] = useState<string>('');
  const [newClientData, setNewClientData] = useState<NewClientData>({
    email: '',
    firstName: claim.firstName || '',
    lastName: claim.lastName || '',
    phoneNumber: claim.phoneNumber || '',
    addressLine1: claim.addressLine1 || '',
    addressLine2: claim.addressLine2 || '',
    city: claim.city || claim.town || '',
    postcode: claim.postcode || ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch related claims when modal opens
  useEffect(() => {
    if (isOpen && claim.id) {
      setIsLoading(true);
      setError(null);
      console.log('[SplitClaimsModal] Fetching related claims for:', claim.id);
      getRelatedClaims(claim.id)
        .then((response) => {
          console.log('[SplitClaimsModal] Got related claims:', response);
          setRelatedClaims(response.claims);
          // Get client name from the first claim
          if (response.claims.length > 0) {
            const first = response.claims[0];
            const name = [first.firstName, first.lastName].filter(Boolean).join(' ') || 'Unknown';
            setClientName(name);
            setClientEmail(first.email || '');
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('[SplitClaimsModal] Failed to fetch related claims:', err);
          setError('Failed to load related claims');
          setIsLoading(false);
        });
    }
  }, [isOpen, claim.id]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep(1);
      setSelectedClaimIds(new Set());
      setError(null);
      setClientName('');
      setClientEmail('');
      setNewClientData({
        email: claim.email || '',
        firstName: claim.firstName || '',
        lastName: claim.lastName || '',
        phoneNumber: claim.phoneNumber || '',
        addressLine1: claim.addressLine1 || '',
        addressLine2: claim.addressLine2 || '',
        city: claim.city || claim.town || '',
        postcode: claim.postcode || ''
      });
    }
  }, [isOpen, claim]);

  const handleClaimToggle = (claimId: string) => {
    const newSelected = new Set(selectedClaimIds);
    if (newSelected.has(claimId)) {
      newSelected.delete(claimId);
    } else {
      newSelected.add(claimId);
    }
    setSelectedClaimIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedClaimIds.size === relatedClaims.length) {
      setSelectedClaimIds(new Set());
    } else {
      setSelectedClaimIds(new Set(relatedClaims.map(c => c.id)));
    }
  };

  const handleInputChange = (field: keyof NewClientData, value: string) => {
    setNewClientData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const payload: SplitClaimsPayload = {
        claimIds: Array.from(selectedClaimIds),
        newClientData: {
          email: newClientData.email,
          firstName: newClientData.firstName || undefined,
          lastName: newClientData.lastName || undefined,
          phoneNumber: newClientData.phoneNumber || undefined,
          addressLine1: newClientData.addressLine1 || undefined,
          addressLine2: newClientData.addressLine2 || undefined,
          city: newClientData.city || undefined,
          postcode: newClientData.postcode || undefined
        }
      };

      console.log('[SplitClaimsModal] Submitting split request with payload:', payload);
      const result = await splitClaims(payload);
      console.log('[SplitClaimsModal] Split result:', result);

      if (result.success) {
        console.log('[SplitClaimsModal] Split successful, calling onSuccess with email:', newClientData.email);
        onSuccess(newClientData.email);
        onClose();
      } else {
        console.error('[SplitClaimsModal] Split returned success=false:', result.message);
        setError(result.message || 'Split failed');
      }
    } catch (err: any) {
      console.error('[SplitClaimsModal] Failed to split claims:', err);
      console.error('[SplitClaimsModal] Error response:', err.response?.data);
      setError(err.response?.data?.error || err.message || 'Failed to split claims');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedToStep2 = selectedClaimIds.size > 0;
  const canProceedToStep3 = newClientData.email.trim() !== '';

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB');
    } catch {
      return dateStr;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="split-claims-modal-overlay" onClick={onClose}>
      <div className="split-claims-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Split Claims</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {/* Step Indicator */}
        <div className="step-indicator">
          <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <span className="step-number">1</span>
            <span className="step-label">Select Claims</span>
          </div>
          <div className="step-line" />
          <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <span className="step-number">2</span>
            <span className="step-label">Client Details</span>
          </div>
          <div className="step-line" />
          <div className={`step ${step >= 3 ? 'active' : ''}`}>
            <span className="step-number">3</span>
            <span className="step-label">Confirm</span>
          </div>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          {/* Step 1: Select Claims */}
          {step === 1 && (
            <div className="step-content">
              {clientName && (
                <div className="client-info-banner">
                  <strong>Current Client:</strong> {clientName}
                  {clientEmail && <span className="client-email"> ({clientEmail})</span>}
                </div>
              )}
              <p className="step-description">
                Select the claims you want to split off to a new client. These claims will be moved to a new client record.
              </p>

              {isLoading ? (
                <div className="loading">Loading related claims...</div>
              ) : relatedClaims.length === 0 ? (
                <div className="no-claims">No related claims found for this client.</div>
              ) : (
                <>
                  <div className="select-all-row">
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedClaimIds.size === relatedClaims.length}
                        onChange={handleSelectAll}
                      />
                      <span>Select All ({relatedClaims.length} claims)</span>
                    </label>
                  </div>

                  <div className="claims-table">
                    <table>
                      <thead>
                        <tr>
                          <th></th>
                          <th>Reference</th>
                          <th>Lender</th>
                          <th>Status</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relatedClaims.map((relatedClaim) => (
                          <tr
                            key={relatedClaim.id}
                            className={selectedClaimIds.has(relatedClaim.id) ? 'selected' : ''}
                            onClick={() => handleClaimToggle(relatedClaim.id)}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedClaimIds.has(relatedClaim.id)}
                                onChange={() => handleClaimToggle(relatedClaim.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td>{relatedClaim.proclaimReference || relatedClaim.id}</td>
                            <td>{relatedClaim.craFinanceLender || '-'}</td>
                            <td>
                              <span className={`status-badge status-${(relatedClaim.status || 'unknown').toLowerCase()}`}>
                                {relatedClaim.status || 'Unknown'}
                              </span>
                            </td>
                            <td>{formatDate(relatedClaim.createdInApiAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: Client Details */}
          {step === 2 && (
            <div className="step-content">
              <p className="step-description">
                Enter the details for the new client. Keep the email the same if the two people share a household account, or change it if they have separate addresses.
              </p>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  id="email"
                  type="email"
                  value={newClientData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="form-control"
                  placeholder="email@example.com"
                  required
                />
                <p className="form-hint">May be shared with the original client if both real people use the same inbox.</p>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    id="firstName"
                    type="text"
                    value={newClientData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    id="lastName"
                    type="text"
                    value={newClientData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number</label>
                <input
                  id="phoneNumber"
                  type="tel"
                  value={newClientData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="addressLine1">Address Line 1</label>
                <input
                  id="addressLine1"
                  type="text"
                  value={newClientData.addressLine1}
                  onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="addressLine2">Address Line 2</label>
                <input
                  id="addressLine2"
                  type="text"
                  value={newClientData.addressLine2}
                  onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                  className="form-control"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    id="city"
                    type="text"
                    value={newClientData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="postcode">Postcode</label>
                  <input
                    id="postcode"
                    type="text"
                    value={newClientData.postcode}
                    onChange={(e) => handleInputChange('postcode', e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="step-content">
              <p className="step-description">
                Please review the split details below before confirming.
              </p>

              <div className="confirmation-summary">
                <div className="summary-section">
                  <h4>Claims to Split</h4>
                  <p><strong>{selectedClaimIds.size}</strong> claim(s) will be moved to the new client:</p>
                  <ul className="claims-list">
                    {Array.from(selectedClaimIds).map(id => {
                      const claim = relatedClaims.find(c => c.id === id);
                      return (
                        <li key={id}>
                          {claim?.proclaimReference || id} - {claim?.craFinanceLender || 'Unknown Lender'}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="summary-section">
                  <h4>New Client Details</h4>
                  <dl>
                    <dt>Email:</dt>
                    <dd>{newClientData.email}</dd>
                    <dt>Name:</dt>
                    <dd>{newClientData.firstName} {newClientData.lastName}</dd>
                    {newClientData.phoneNumber && (
                      <>
                        <dt>Phone:</dt>
                        <dd>{newClientData.phoneNumber}</dd>
                      </>
                    )}
                    {newClientData.addressLine1 && (
                      <>
                        <dt>Address:</dt>
                        <dd>
                          {newClientData.addressLine1}
                          {newClientData.addressLine2 && <>, {newClientData.addressLine2}</>}
                          {newClientData.city && <>, {newClientData.city}</>}
                          {newClientData.postcode && <> {newClientData.postcode}</>}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>

                <div className="warning-box">
                  <strong>Important:</strong> The new client will need to complete ID verification.
                  All correspondence, documents, and tasks associated with these claims will remain with the claims.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step > 1 && (
            <button
              className="btn btn-secondary"
              onClick={() => setStep((step - 1) as Step)}
              disabled={isSubmitting}
            >
              Back
            </button>
          )}

          <button className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>

          {step < 3 ? (
            <button
              className="btn btn-primary"
              onClick={() => setStep((step + 1) as Step)}
              disabled={step === 1 ? !canProceedToStep2 : !canProceedToStep3}
            >
              Next
            </button>
          ) : (
            <button
              className="btn btn-primary btn-danger"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Splitting...' : 'Confirm Split'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SplitClaimsModal;
